<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import { button as greenButton } from '@apparatix/theme/Green';
import { getExpirationStatusSeverity } from '@apparatix/composables';
import type { ExpirationRow } from '@apparatix/types/Expirations';

type Props = {
  visible: boolean;
  row?: ExpirationRow;
  reasons: string[];
  canAct: boolean;
  isActing: boolean;
}

type Emits = {
  (e: 'close'): void;
  (e: 'on:renew'): void;
  (e: 'on:not-renew', reason: string): void;
  (e: 'on:ignore-subcontract'): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const selectedReason = ref<string>();

const isVisible = computed({
  get: () => props.visible,
  set: () => emit('close'),
});

watch(() => props.visible, () => selectedReason.value = undefined);

const canCreateRenewal = computed(() =>
  props.canAct && ['Expiring', 'Expired', 'Not Renewed', 'Subcontract ignored'].includes(props.row?.status));

const canDecideNotRenewing = computed(() =>
  props.canAct && (props.row?.status === 'Expiring' || props.row?.status === 'Expired'));

const endDateDisplay = computed(() =>
  props.row?.isMonthToMonth ? `${props.row.endDate} (M)` : props.row?.endDate);

const onNotRenew = () => {
  if (!selectedReason.value) return;
  emit('on:not-renew', selectedReason.value);
};
</script>
<template>
  <Dialog
    v-model:visible="isVisible"
    header="Contract Expiration"
    style="width: 33vw"
    :breakpoints="{ '1280px': '50vw', '960px': '75vw', '640px': '95vw' }"
    modal
  >
    <div v-if="row" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-lg font-semibold">{{ row.accountName }}</span>
          <Tag :value="row.status" :severity="getExpirationStatusSeverity(row.status)" />
        </div>
        <span class="text-sm text-[var(--black-80)]">Contract {{ row.contractId }} &middot; {{ row.status === 'Expired' ? 'Ended' : 'Ends' }} {{ endDateDisplay }}</span>
        <span v-if="row.isSubcontract" class="text-sm text-[var(--black-80)]">Subcontract flight</span>
        <span v-if="row.notes" class="text-sm italic text-[var(--black-80)]">{{ row.notes }}</span>
      </div>
      <template v-if="canCreateRenewal">
        <p class="text-sm font-normal">
          {{ canDecideNotRenewing
            ? 'Create a renewal opportunity for this contract, or mark it as not renewing.'
            : 'Create a renewal opportunity for this contract.' }}
        </p>
        <div class="flex flex-wrap gap-2 justify-end items-center">
          <Select
            v-if="canDecideNotRenewing && !row.isSubcontract"
            v-model="selectedReason"
            :options="reasons"
            placeholder="Not Renewing - Select Reason..."
            class="grow basis-full sm:basis-0 min-w-0"
            :disabled="isActing"
            @change="onNotRenew"
          />
          <Button
            v-if="canDecideNotRenewing && row.isSubcontract"
            severity="danger"
            size="small"
            :disabled="isActing"
            @click="emit('on:ignore-subcontract')"
          >
            Ignore Subcontract
          </Button>
          <Button
            :dt="greenButton"
            size="small"
            :disabled="isActing || !!selectedReason"
            @click="emit('on:renew')"
          >
            Create Renewal
          </Button>
        </div>
      </template>
      <template v-else>
        <p class="text-sm font-normal">
          No actions are available for this contract.
        </p>
        <div class="flex gap-2 justify-end items-center">
          <Button
            severity="secondary"
            size="small"
            @click="emit('close')"
          >
            Close
          </Button>
        </div>
      </template>
    </div>
  </Dialog>
</template>
